/**
 * BIGWA - Mapeamento Universal de Tipos de Arquivo
 * v1.0 - Janeiro 2026
 * 
 * Detecta categoria de arquivo pela extensão e fornece
 * configurações para ícone, cor e organização no Drive.
 */

export type FileCategory = 
  | 'audio' 
  | 'video' 
  | 'image' 
  | 'document' 
  | 'tabular' 
  | 'presentation' 
  | 'other';

export interface FileTypeConfig {
  category: FileCategory;
  icon: string;        // Nome do ícone Lucide
  color: string;       // Cor hex para UI
  label: string;       // Label em português
  driveFolder: string; // Subpasta no Google Drive
}

/**
 * Mapeamento de extensões para configurações de tipo
 */
export const FILE_TYPE_MAP: Record<string, FileTypeConfig> = {
  // ═══════════════════════════════════════════
  // ÁUDIO
  // ═══════════════════════════════════════════
  'mp3':  { category: 'audio', icon: 'Mic', color: '#9333EA', label: 'Áudio', driveFolder: 'Audio' },
  'wav':  { category: 'audio', icon: 'Mic', color: '#9333EA', label: 'Áudio', driveFolder: 'Audio' },
  'm4a':  { category: 'audio', icon: 'Mic', color: '#9333EA', label: 'Áudio', driveFolder: 'Audio' },
  'ogg':  { category: 'audio', icon: 'Mic', color: '#9333EA', label: 'Áudio', driveFolder: 'Audio' },
  'aac':  { category: 'audio', icon: 'Mic', color: '#9333EA', label: 'Áudio', driveFolder: 'Audio' },
  'wma':  { category: 'audio', icon: 'Mic', color: '#9333EA', label: 'Áudio', driveFolder: 'Audio' },
  'flac': { category: 'audio', icon: 'Mic', color: '#9333EA', label: 'Áudio', driveFolder: 'Audio' },
  
  // ═══════════════════════════════════════════
  // VÍDEO
  // ═══════════════════════════════════════════
  'mp4':  { category: 'video', icon: 'Video', color: '#DC2626', label: 'Vídeo', driveFolder: 'Video' },
  'mov':  { category: 'video', icon: 'Video', color: '#DC2626', label: 'Vídeo', driveFolder: 'Video' },
  'avi':  { category: 'video', icon: 'Video', color: '#DC2626', label: 'Vídeo', driveFolder: 'Video' },
  'mkv':  { category: 'video', icon: 'Video', color: '#DC2626', label: 'Vídeo', driveFolder: 'Video' },
  'webm': { category: 'video', icon: 'Video', color: '#DC2626', label: 'Vídeo', driveFolder: 'Video' },
  'wmv':  { category: 'video', icon: 'Video', color: '#DC2626', label: 'Vídeo', driveFolder: 'Video' },
  'm4v':  { category: 'video', icon: 'Video', color: '#DC2626', label: 'Vídeo', driveFolder: 'Video' },
  
  // ═══════════════════════════════════════════
  // IMAGEM
  // ═══════════════════════════════════════════
  'jpg':  { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'jpeg': { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'png':  { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'gif':  { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'tiff': { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'tif':  { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'raw':  { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'bmp':  { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'webp': { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'svg':  { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  'heic': { category: 'image', icon: 'Image', color: '#16A34A', label: 'Imagem', driveFolder: 'Imagem' },
  
  // ═══════════════════════════════════════════
  // DOCUMENTO
  // ═══════════════════════════════════════════
  'pdf':  { category: 'document', icon: 'FileText', color: '#2563EB', label: 'Documento', driveFolder: 'Documento' },
  'doc':  { category: 'document', icon: 'FileText', color: '#2563EB', label: 'Documento', driveFolder: 'Documento' },
  'docx': { category: 'document', icon: 'FileText', color: '#2563EB', label: 'Documento', driveFolder: 'Documento' },
  'txt':  { category: 'document', icon: 'FileText', color: '#2563EB', label: 'Documento', driveFolder: 'Documento' },
  'rtf':  { category: 'document', icon: 'FileText', color: '#2563EB', label: 'Documento', driveFolder: 'Documento' },
  'odt':  { category: 'document', icon: 'FileText', color: '#2563EB', label: 'Documento', driveFolder: 'Documento' },
  'md':   { category: 'document', icon: 'FileText', color: '#2563EB', label: 'Documento', driveFolder: 'Documento' },
  
  // ═══════════════════════════════════════════
  // DADOS TABULARES
  // ═══════════════════════════════════════════
  'xlsx': { category: 'tabular', icon: 'Table', color: '#EA580C', label: 'Planilha', driveFolder: 'DadosTabulares' },
  'xls':  { category: 'tabular', icon: 'Table', color: '#EA580C', label: 'Planilha', driveFolder: 'DadosTabulares' },
  'csv':  { category: 'tabular', icon: 'Table', color: '#EA580C', label: 'Planilha', driveFolder: 'DadosTabulares' },
  'tsv':  { category: 'tabular', icon: 'Table', color: '#EA580C', label: 'Planilha', driveFolder: 'DadosTabulares' },
  'ods':  { category: 'tabular', icon: 'Table', color: '#EA580C', label: 'Planilha', driveFolder: 'DadosTabulares' },
  
  // ═══════════════════════════════════════════
  // APRESENTAÇÃO
  // ═══════════════════════════════════════════
  'pptx': { category: 'presentation', icon: 'Presentation', color: '#EAB308', label: 'Apresentação', driveFolder: 'Apresentacao' },
  'ppt':  { category: 'presentation', icon: 'Presentation', color: '#EAB308', label: 'Apresentação', driveFolder: 'Apresentacao' },
  'key':  { category: 'presentation', icon: 'Presentation', color: '#EAB308', label: 'Apresentação', driveFolder: 'Apresentacao' },
  'odp':  { category: 'presentation', icon: 'Presentation', color: '#EAB308', label: 'Apresentação', driveFolder: 'Apresentacao' },
};

/**
 * Configuração padrão para tipos desconhecidos
 */
export const DEFAULT_FILE_CONFIG: FileTypeConfig = {
  category: 'other',
  icon: 'File',
  color: '#6B7280',
  label: 'Outro',
  driveFolder: 'Outros'
};

/**
 * Extrai a extensão de um nome de arquivo
 * @param filename Nome do arquivo (ex: "documento.pdf")
 * @returns Extensão em minúsculas (ex: "pdf")
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  if (parts.length < 2) return '';
  return parts[parts.length - 1].toLowerCase();
}

/**
 * Obtém a configuração de tipo para um arquivo
 * @param filename Nome do arquivo ou extensão
 * @returns Configuração do tipo de arquivo
 */
export function getFileTypeConfig(filename: string): FileTypeConfig {
  const ext = getFileExtension(filename);
  return FILE_TYPE_MAP[ext] || DEFAULT_FILE_CONFIG;
}

/**
 * Verifica se um arquivo é de uma categoria específica
 * @param filename Nome do arquivo
 * @param category Categoria a verificar
 */
export function isFileCategory(filename: string, category: FileCategory): boolean {
  return getFileTypeConfig(filename).category === category;
}

/**
 * Obtém todas as extensões de uma categoria
 * @param category Categoria desejada
 * @returns Array de extensões
 */
export function getExtensionsByCategory(category: FileCategory): string[] {
  return Object.entries(FILE_TYPE_MAP)
    .filter(([_, config]) => config.category === category)
    .map(([ext, _]) => ext);
}

/**
 * Formata tamanho de arquivo para exibição
 * @param bytes Tamanho em bytes
 * @returns String formatada (ex: "15.2 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Limite de tamanho de arquivo (5GB)
 * Atualizado em Janeiro 2026 para suportar vídeos e áudios longos
 * O upload resumível suporta até 5TB, mas 5GB é limite prático
 */
export const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB em bytes

/**
 * Verifica se o arquivo está dentro do limite de tamanho
 * @param size Tamanho em bytes
 * @returns true se está dentro do limite
 */
export function isFileSizeValid(size: number): boolean {
  return size <= MAX_FILE_SIZE;
}

/**
 * Mapeamento de ícones Lucide por categoria
 * (Para importação dinâmica se necessário)
 */
export const CATEGORY_ICONS = {
  audio: 'Mic',
  video: 'Video',
  image: 'Image',
  document: 'FileText',
  tabular: 'Table',
  presentation: 'Presentation',
  other: 'File'
} as const;

/**
 * Mapeamento de cores por categoria
 */
export const CATEGORY_COLORS = {
  audio: '#9333EA',      // Roxo
  video: '#DC2626',      // Vermelho
  image: '#16A34A',      // Verde
  document: '#2563EB',   // Azul
  tabular: '#EA580C',    // Laranja
  presentation: '#EAB308', // Amarelo
  other: '#6B7280'       // Cinza
} as const;

/**
 * Labels em português por categoria
 */
export const CATEGORY_LABELS = {
  audio: 'Áudio',
  video: 'Vídeo',
  image: 'Imagem',
  document: 'Documento',
  tabular: 'Planilha',
  presentation: 'Apresentação',
  other: 'Outro'
} as const;
